import { StargraveBuff } from '../../types';

// Buff catalog derived from core-book powers/technology with ongoing effects.
// Buffs without statModifiers are informational (conditional or non-stat effects).
export const stargraveBuffs: StargraveBuff[] = [
  {
    id: 'armour-plates', name: 'Armour Plates', source: 'power', sourceId: 'armour-plates',
    statModifiers: { armour: 2 },
    summary: '+2 Armour. May not be used if the figure is already wearing combat armour.',
  },
  {
    id: 'armoury-weapon-boost', name: 'Armoury: Weapon Boost', source: 'power', sourceId: 'armoury',
    displayEffect: '+1 Damage (one pistol/carbine/shotgun)',
    summary: 'Armoury: one standard (not Advanced Technology) pistol, carbine, or shotgun has +1 Damage for this game only.',
  },
  {
    id: 'weapon-locker-boost', name: 'Weapon Locker', source: 'manual', sourceId: 'weapon-locker',
    displayEffect: '+1 Damage pistol/carbine/shotgun (uses a gear slot)',
    summary: 'Weapon Locker: this soldier carries a +1 Damage pistol, carbine, or shotgun that occupies a gear slot for this game.',
  },
  {
    id: 'coordinated-fire', name: 'Coordinated Fire', source: 'power', sourceId: 'coordinated-fire',
    statModifiers: { shoot: 1 }, statMaximums: { shoot: 5 },
    summary: '+1 Shoot for the duration of the game (to a maximum of +5). A figure may only benefit from one Coordinated Fire at a time.',
  },
  {
    id: 'target-designation', name: 'Target Designation', source: 'power', sourceId: 'target-designation',
    displayEffect: '-2 Fight vs Shooting',
    summary: 'For the rest of the battle, this figure receives -2 Fight whenever rolling against a Shooting attack.',
  },
  {
    id: 'camouflage', name: 'Camouflage', source: 'power', sourceId: 'camouflage',
    displayEffect: '+2 Fight vs Shooting',
    summary: 'No line of sight beyond 12". +2 Fight against Shooting attacks from pistol, carbine, shotgun, or rapid-fire. Cancelled if stunned.',
  },
  {
    id: 'void-blade', name: 'Void Blade', source: 'power', sourceId: 'void-blade',
    displayEffect: '+3 Fight vs Shooting',
    summary: 'Hand weapon becomes indestructible, +2 Damage. +3 Fight against Shooting from pistols, carbines, rapid-fire, or shotguns. Ends if stunned.',
  },
  {
    id: 'energy-shield', name: 'Energy Shield', source: 'power', sourceId: 'energy-shield',
    displayEffect: 'Absorbs next 3 Damage from Shooting',
    summary: 'Absorbs the next 3 points of Damage from any Shooting attack, then the power is cancelled.',
  },
  {
    id: 'psychic-shield', name: 'Psychic Shield', source: 'power', sourceId: 'psychic-shield',
    displayEffect: 'Halves next Shooting Damage',
    summary: 'The next Shooting hit that causes Damage is halved (rounding down), then the power is cancelled. Cancelled if in combat.',
  },
  {
    id: 'fortune', name: 'Fortune', source: 'power', sourceId: 'fortune',
    displayEffect: 'Reroll one roll',
    summary: 'Discard to reroll a Combat, Shooting, or Stat Roll. The reroll must be kept. Only one fortune token per figure.',
  },
  {
    id: 'temporary-upgrade-move', name: 'Upgrade: +1 Move', source: 'power', sourceId: 'temporary-upgrade',
    statModifiers: { move: 1 }, statMaximums: { move: 7 }, exclusiveGroup: 'temporary-upgrade',
    summary: 'Temporary Upgrade: +1 Move (maximum Move 7). Only one upgrade active at a time.',
  },
  {
    id: 'temporary-upgrade-fight', name: 'Upgrade: +1 Fight', source: 'power', sourceId: 'temporary-upgrade',
    statModifiers: { fight: 1 }, statMaximums: { fight: 6 }, exclusiveGroup: 'temporary-upgrade',
    summary: 'Temporary Upgrade: +1 Fight (maximum +6). Only one upgrade active at a time.',
  },
  {
    id: 'temporary-upgrade-shoot', name: 'Upgrade: +1 Shoot', source: 'power', sourceId: 'temporary-upgrade',
    statModifiers: { shoot: 1 }, statMaximums: { shoot: 6 }, exclusiveGroup: 'temporary-upgrade',
    summary: 'Temporary Upgrade: +1 Shoot (maximum +6). Only one upgrade active at a time.',
  },
  {
    id: 'temporary-upgrade-will', name: 'Upgrade: +3 Will', source: 'power', sourceId: 'temporary-upgrade',
    statModifiers: { will: 3 }, statMaximums: { will: 8 }, exclusiveGroup: 'temporary-upgrade',
    summary: 'Temporary Upgrade: +3 Will (maximum +8). Only one upgrade active at a time.',
  },
  {
    id: 'temporary-upgrade-armour', name: 'Upgrade: +1 Armour', source: 'power', sourceId: 'temporary-upgrade',
    statModifiers: { armour: 1 }, statMaximums: { armour: 14 }, exclusiveGroup: 'temporary-upgrade',
    summary: 'Temporary Upgrade: +1 Armour (maximum 14). Only one upgrade active at a time.',
  },
  {
    id: 'combat-drugs', name: 'Combat Drugs', source: 'technology', sourceId: 'combat-drugs',
    statModifiers: { fight: 1, will: -1 }, statMaximums: { fight: 6 },
    summary: '+1 Fight (maximum +6) and -1 Will for one game.',
  },
  {
    id: 'improved-drone', name: 'Improved Drone', source: 'technology', sourceId: 'improved-drone',
    statModifiers: { move: 1, fight: 1 },
    summary: 'Once per game: +1 Move and +1 Fight for the activation. Remove the buff when the activation ends.',
  },
  {
    id: 're-wire-robot-move', name: 'Re-wire: +1 Move', source: 'power', sourceId: 're-wire-robot',
    statModifiers: { move: 1, will: -1 }, exclusiveGroup: 're-wire-robot',
    summary: 'Re-wired robot: +1 Move, -1 Will. No robot may be re-wired more than once.',
  },
  {
    id: 're-wire-robot-fight', name: 'Re-wire: +1 Fight', source: 'power', sourceId: 're-wire-robot',
    statModifiers: { fight: 1, will: -1 }, exclusiveGroup: 're-wire-robot',
    summary: 'Re-wired robot: +1 Fight, -1 Will. No robot may be re-wired more than once.',
  },
  {
    id: 're-wire-robot-armour', name: 'Re-wire: +1 Armour', source: 'power', sourceId: 're-wire-robot',
    statModifiers: { armour: 1, will: -1 }, exclusiveGroup: 're-wire-robot',
    summary: 'Re-wired robot: +1 Armour, -1 Will. No robot may be re-wired more than once.',
  },
];

export const getStargraveBuffById = (buffId: string): StargraveBuff | undefined =>
  stargraveBuffs.find((buff) => buff.id === buffId);
// Prefer game/stargraveBuffRules.getStargraveBuffById, which includes custom buffs.
